import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, X, Clock, CalendarDays, MapPin, Plus, Minus, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/site/PageHero";
import { Reveal } from "@/components/motion";
import { useEnquiry } from "@/context/EnquiryContext";
import { getTour } from "@/lib/api";

export default function TourDetail() {
  const { slug } = useParams();
  const [tour, setTour] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [openFaq, setOpenFaq] = useState(0);
  const { openEnquiry } = useEnquiry();

  useEffect(() => {
    window.scrollTo(0, 0);
    setTour(null); setNotFound(false);
    getTour(slug).then(setTour).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="pt-40 pb-40 text-center">
      <h1 className="font-serif text-4xl text-[#3b1a1a]">Tour not found</h1>
      <Link to="/tours" className="mt-6 inline-block text-[#7c1d1d] tracking-[0.2em] uppercase text-xs">← Back to tours</Link>
    </div>
  );
  if (!tour) return <div className="pt-40 pb-40 text-center text-[#5a4b4b]">Loading…</div>;

  const schema = {
    "@context": "https://schema.org",
    "@type": "TouristTrip",
    name: tour.name,
    description: tour.overview,
    image: tour.banner_image,
    touristType: tour.category,
  };

  return (
    <>
      <Seo title={tour.seo?.meta_title || `${tour.name} | Legacy India Travel`} description={tour.seo?.meta_description || tour.overview} image={tour.banner_image} type="article" schema={schema} />
      <PageHero title={tour.name} subtitle={tour.destinations?.join(" · ")} image={tour.banner_image} crumbs={[{ label: "Tours", to: "/tours" }, { label: tour.name }]} />

      {/* quick facts bar */}
      <div className="bg-[#14523d] text-white">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-5 flex flex-wrap gap-x-10 gap-y-3 items-center justify-between">
          <div className="flex flex-wrap gap-x-10 gap-y-3">
            {tour.duration_label && <Fact icon={Clock} label="Duration" value={tour.duration_label} />}
            {tour.best_time && <Fact icon={CalendarDays} label="Best Time" value={tour.best_time} />}
            <Fact icon={MapPin} label="Category" value={tour.category} />
          </div>
          <button onClick={() => openEnquiry({ preferred_tour: tour.name })} data-testid="tour-enquire-top" className="bg-[#d4af37] hover:bg-[#e8c970] text-[#2d1616] px-6 py-3 text-[11px] tracking-[0.22em] uppercase font-semibold transition-colors">
            Enquire Now
          </button>
        </div>
      </div>

      <section className="py-16 sm:py-24 bg-[#fdfbf7]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Main */}
          <div className="lg:col-span-8 space-y-16">
            {/* Overview */}
            <Reveal>
              <h2 className="font-serif text-3xl sm:text-4xl text-[#3b1a1a]">Overview</h2>
              <p className="mt-5 text-[#4a4a4a] text-lg leading-relaxed">{tour.overview}</p>
            </Reveal>

            {/* Highlights */}
            {tour.highlights?.length > 0 && (
              <Reveal>
                <h2 className="font-serif text-3xl sm:text-4xl text-[#3b1a1a]">Highlights</h2>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {tour.highlights.map((h, i) => (
                    <div key={i} className="flex items-start gap-3 bg-white border border-[#d4af37]/25 p-4">
                      <span className="mt-0.5 w-6 h-6 shrink-0 rounded-full bg-[#7c1d1d] text-white grid place-items-center"><Check className="w-3.5 h-3.5" /></span>
                      <span className="text-[#3b1a1a]">{h}</span>
                    </div>
                  ))}
                </div>
              </Reveal>
            )}

            {/* Itinerary */}
            {tour.itinerary?.length > 0 && (
              <Reveal>
                <h2 className="font-serif text-3xl sm:text-4xl text-[#3b1a1a]">Day-by-Day Itinerary</h2>
                <div className="mt-8 relative border-l-2 border-[#d4af37]/40 ml-3">
                  {tour.itinerary.map((d, i) => (
                    <div key={i} className="relative pl-8 pb-10 last:pb-0" data-testid={`itinerary-day-${d.day}`}>
                      <span className="absolute -left-[13px] top-0 w-6 h-6 rounded-full bg-[#7c1d1d] text-white text-[11px] grid place-items-center font-semibold">{d.day}</span>
                      <div className="text-[10px] tracking-[0.24em] uppercase text-[#14523d]">Day {d.day}</div>
                      <h3 className="font-serif text-2xl text-[#3b1a1a] mt-1">{d.title}</h3>
                      {d.description && <p className="text-[#5a4b4b] mt-2 leading-relaxed">{d.description}</p>}
                    </div>
                  ))}
                </div>
              </Reveal>
            )}

            {/* Gallery */}
            {tour.gallery?.length > 0 && (
              <Reveal>
                <h2 className="font-serif text-3xl sm:text-4xl text-[#3b1a1a]">Photo Gallery</h2>
                <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {tour.gallery.map((g, i) => (
                    <button key={i} onClick={() => setLightbox(g)} className="relative overflow-hidden aspect-[4/3] group border border-[#d4af37]/25">
                      <img src={g} alt={`${tour.name} ${i + 1}`} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                    </button>
                  ))}
                </div>
              </Reveal>
            )}

            {/* Inclusions / Exclusions */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              {tour.inclusions?.length > 0 && (
                <Reveal>
                  <h3 className="font-serif text-2xl text-[#14523d]">Inclusions</h3>
                  <ul className="mt-4 space-y-2.5">
                    {tour.inclusions.map((x, i) => <li key={i} className="flex items-start gap-2.5 text-[#4a4a4a]"><Check className="w-4 h-4 text-[#14523d] mt-1 shrink-0" /> {x}</li>)}
                  </ul>
                </Reveal>
              )}
              {tour.exclusions?.length > 0 && (
                <Reveal>
                  <h3 className="font-serif text-2xl text-[#7c1d1d]">Exclusions</h3>
                  <ul className="mt-4 space-y-2.5">
                    {tour.exclusions.map((x, i) => <li key={i} className="flex items-start gap-2.5 text-[#4a4a4a]"><X className="w-4 h-4 text-[#7c1d1d] mt-1 shrink-0" /> {x}</li>)}
                  </ul>
                </Reveal>
              )}
            </div>

            {/* FAQ */}
            {tour.faqs?.length > 0 && (
              <Reveal>
                <h2 className="font-serif text-3xl sm:text-4xl text-[#3b1a1a]">Frequently Asked Questions</h2>
                <div className="mt-6 border-t border-[#d4af37]/25">
                  {tour.faqs.map((f, i) => (
                    <div key={i} className="border-b border-[#d4af37]/25">
                      <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full flex items-center justify-between gap-4 py-5 text-left">
                        <span className="font-serif text-xl text-[#3b1a1a]">{f.question}</span>
                        <span className="shrink-0 w-8 h-8 border border-[#7c1d1d]/40 grid place-items-center text-[#7c1d1d]">{openFaq === i ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}</span>
                      </button>
                      <AnimatePresence initial={false}>
                        {openFaq === i && <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden"><p className="pb-5 text-[#5a4b4b] leading-relaxed">{f.answer}</p></motion.div>}
                      </AnimatePresence>
                    </div>
                  ))}
                </div>
              </Reveal>
            )}
          </div>

          {/* Sticky sidebar */}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-24 space-y-6">
              <div className="bg-white border border-[#d4af37]/40 p-7 shadow-[0_20px_50px_rgba(124,29,29,0.08)]">
                <div className="text-[11px] tracking-[0.24em] uppercase text-[#14523d]">Interested in this journey?</div>
                <h3 className="font-serif text-3xl text-[#3b1a1a] mt-2 leading-tight">Get a tailored plan</h3>
                <p className="text-sm text-[#5a4b4b] mt-3 leading-relaxed">We don’t do fixed prices — we craft a personalised itinerary and quote around your dates and interests.</p>
                <button onClick={() => openEnquiry({ preferred_tour: tour.name })} data-testid="tour-enquire-side" className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#7c1d1d] hover:bg-[#5a1515] text-white px-6 py-4 text-xs tracking-[0.24em] uppercase transition-colors">
                  Enquire About This Tour <ArrowRight className="w-4 h-4" />
                </button>
              </div>
              {tour.map_embed && (
                <div className="border border-[#d4af37]/40 overflow-hidden aspect-square">
                  <iframe title="map" src={tour.map_embed} className="w-full h-full" loading="lazy" />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setLightbox(null)} className="fixed inset-0 z-[90] bg-black/90 flex items-center justify-center p-6">
            <button className="absolute top-6 right-6 text-white w-12 h-12 grid place-items-center border border-white/40"><X className="w-6 h-6" /></button>
            <img src={lightbox} alt="Gallery" className="max-w-full max-h-[88vh] object-contain" />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function Fact({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3">
      <Icon className="w-5 h-5 text-[#e8c970]" />
      <div className="leading-tight">
        <div className="text-[10px] tracking-[0.2em] uppercase text-[#e8c970]/80">{label}</div>
        <div className="text-sm font-medium">{value}</div>
      </div>
    </div>
  );
}
